// Top-level route table.
//
// Marketing routes share <MarketingLayout> (header + footer + scroll reveal).
// /login and /signup also render inside MarketingLayout so the chrome stays
// consistent across the unauthenticated surface.
//
// The authed surface has TWO chromes, both gated by RequireAuth:
//   - <AppShell>  — brand bar + work area for the non-boat routes (the
//     multi-boat switcher, profile, activate).
//   - <BoatShell> — brand bar + boat-scoped nav (desktop rail + mobile bottom
//     tab bar) + the manual search/sheet overlays, for everything under a boat.

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ModeBoardRedirect } from './pages/ModeBoardRedirect';

import { AdminApp } from './admin/AdminApp';
import { AppShell } from './components/app/AppShell';
import { BoatShell } from './components/app/BoatShell';
import { MarketingLayout } from './components/MarketingLayout';
import { DemoLeaseWatcher, GuestBlocked } from './components/DemoSession';
import { RequireAuth } from './components/RequireAuth';
import { useLegacyUnitMigration } from './lib/useLegacyUnitMigration';
import { About } from './pages/About';
import { Activate } from './pages/Activate';
import { AlarmDetail } from './pages/AlarmDetail';
import { AlarmNew } from './pages/AlarmNew';
import { AlarmNewCustom } from './pages/AlarmNewCustom';
import { Alarms } from './pages/Alarms';
import { AlarmsRegenerate } from './pages/AlarmsRegenerate';
import { AppSignedIn } from './pages/AppSignedIn';
import { OpenFromApp } from './pages/OpenFromApp';
import { BoatHome } from './pages/BoatHome';
import { BoatSettings } from './pages/BoatSettings';
import { BetaSignup } from './pages/BetaSignup';
import { Contact } from './pages/Contact';
import { Dashboard } from './pages/Dashboard';
import { DeviceApprove } from './pages/DeviceApprove';
import { DeviceDetail } from './pages/DeviceDetail';
import { Devices } from './pages/Devices';
import { Equipment } from './pages/Equipment';
import { LinkedDevices } from './pages/LinkedDevices';
import { Downloads } from './pages/Downloads';
import { ForgotPassword } from './pages/ForgotPassword';
import { Home } from './pages/Home';
import { LinkReturn } from './pages/LinkReturn';
import { Login } from './pages/Login';
import { Displays } from './pages/Displays';
import { Manuals } from './pages/Manuals';
import { NotFound } from './pages/NotFound';
import { OauthConsent } from './pages/OauthConsent';
import { Chats } from './pages/Chats';
import { NeoPage } from './pages/NeoPage';
import { Privacy } from './pages/Privacy';
import { Products } from './pages/Products';
import { Profile } from './pages/Profile';
import { ResetPassword } from './pages/ResetPassword';
import { InviteAccept } from './pages/InviteAccept';
import { RevealEntry } from './pages/RevealEntry';
import { Signup } from './pages/Signup';
import { Status } from './pages/Status';
import { StatusIncident } from './pages/StatusIncident';
import { Support } from './pages/Support';
import { Terms } from './pages/Terms';
import { TryIt } from './pages/TryIt';

// Route-level code split (ADR 0127): the instruments page carries the
// chart-heavy dashboard — no other route pays for it.
const MarineToolkit = lazy(() =>
  import('./pages/MarineToolkit').then((module) => ({ default: module.MarineToolkit }))
);
// The AI Watch Assistant (ADR 0196) carries the chat + debug surface — split
// so the boat chrome doesn't pay for react-markdown until it's opened.
const Board = lazy(() => import('./pages/Board').then((m) => ({ default: m.Board })));
const YourBoards = lazy(() =>
  import('./pages/YourBoards').then((m) => ({ default: m.YourBoards }))
);
// The boat's cameras (TOM-797): the live player and hls.js load with the page.
const Cameras = lazy(() => import('./pages/Cameras').then((m) => ({ default: m.Cameras })));
const WatchAssistant = lazy(() =>
  import('./pages/WatchAssistant').then((m) => ({ default: m.WatchAssistant }))
);

// The chart library's living spec (unlisted, ADR 0131) — dev/design surface,
// static sample data, no auth or boat context needed.
const ChartLab = lazy(() => import('./pages/ChartLab').then((m) => ({ default: m.ChartLab })));
// Composed helm view — the worked example of building a full instrument
// surface from the chart library (unlisted, static data).
const HelmDemo = lazy(() => import('./pages/HelmDemo').then((m) => ({ default: m.HelmDemo })));
const UnderwayDemo = lazy(() => import('./pages/UnderwayDemo'));

export function App() {
  // The one mount of the retired unit toggle's migration: <App> is the single
  // component that lives for the whole session inside <AuthProvider>, and the
  // hook is inert until /v1/me has answered (ADR 0189).
  useLegacyUnitMigration();

  return (
    <>
      {/* A Try it guest whose lease ended goes to /try (TOM-868). */}
      <DemoLeaseWatcher />
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Support />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* The public Sign up buttons lead here (TOM-634, ADR 0384). */}
          <Route path="/beta" element={<BetaSignup />} />
          {/* The home page's Try it opens a demo boat (TOM-868). */}
          <Route path="/try" element={<TryIt />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/link/return" element={<LinkReturn />} />
          {/* The iOS app's Google sign-in ends here (ADR 0350); the app catches it first. */}
          <Route path="/app/signed-in" element={<AppSignedIn />} />
          {/* The iOS app opens a web page as its own account here (TOM-909). */}
          <Route path="/app/open" element={<OpenFromApp />} />
          <Route path="/invites/:inviteId" element={<InviteAccept />} />
          {/* The public status page (TOM-930). */}
          <Route path="/status" element={<Status />} />
          <Route path="/status/incidents/:id" element={<StatusIncident />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Authed, non-boat chrome. */}
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/activate"
            element={
              <GuestBlocked>
                <Activate />
              </GuestBlocked>
            }
          />
          <Route
            path="/device"
            element={
              <GuestBlocked>
                <DeviceApprove />
              </GuestBlocked>
            }
          />
          <Route
            path="/devices"
            element={
              <GuestBlocked>
                <LinkedDevices />
              </GuestBlocked>
            }
          />
        </Route>

        {/* The MCP authorization endpoint's consent screen (TOM-843): the same
          chrome without the assistant. */}
        <Route
          element={
            <RequireAuth>
              <AppShell neo={false} />
            </RequireAuth>
          }
        >
          <Route
            path="/oauth/authorize"
            element={
              <GuestBlocked>
                <OauthConsent />
              </GuestBlocked>
            }
          />
        </Route>

        {/* Boat-scoped chrome, after the Neomaris v2 app prototype (TOM-193). The
          boat opens on home, the mode's board under a fixed header (TOM-510,
          ADR 0335, replacing the full-page chat landing of 2026-10-03); the
          full-page Neo chat is at `neo`. Devices owns inventory and Manuals
          owns the document library. Old /live and /instruments bookmarks lead
          to the mode's board. */}
        <Route
          path="/dashboard/boats/:boatId"
          element={
            <RequireAuth>
              <BoatShell />
            </RequireAuth>
          }
        >
          <Route index element={<BoatHome />} />
          <Route path="neo" element={<NeoPage />} />
          <Route path="neo/:conversationId" element={<NeoPage />} />
          <Route path="chats" element={<Chats />} />
          {/* The Instruments page moved onto system boards (TOM-358): its
            address leads to the board for the boat's mode. */}
          <Route path="instruments" element={<ModeBoardRedirect />} />
          <Route path="alarms" element={<Alarms />} />
          {/* 'new' and 'regenerate' MUST precede ':alarmId' so they aren't
            swallowed as alarm ids. */}
          <Route path="alarms/new" element={<AlarmNew />} />
          <Route path="alarms/new/custom" element={<AlarmNewCustom />} />
          <Route path="alarms/regenerate" element={<AlarmsRegenerate />} />
          <Route path="alarms/:alarmId" element={<AlarmDetail />} />
          <Route
            path="watch"
            element={
              <Suspense
                fallback={
                  <div className="watch-page" aria-busy="true">
                    <p>Loading the watch…</p>
                  </div>
                }
              >
                <WatchAssistant />
              </Suspense>
            }
          />
          {/* Every board by its slug, the boat's list at `boards`, and the old
            `board` address for the main board (TOM-192). */}
          <Route path="board" element={<Navigate to="../boards/main" replace />} />
          <Route
            path="boards"
            element={
              <Suspense
                fallback={
                  <section className="board-page" aria-busy="true">
                    <p className="muted">Loading your boards…</p>
                  </section>
                }
              >
                <YourBoards />
              </Suspense>
            }
          />
          <Route
            path="boards/:slug"
            element={
              <Suspense
                fallback={
                  <section className="board-page" aria-busy="true">
                    <p className="muted">Loading the board…</p>
                  </section>
                }
              >
                <Board />
              </Suspense>
            }
          />
          <Route path="devices" element={<Devices />} />
          <Route path="devices/:deviceId" element={<DeviceDetail />} />
          <Route
            path="cameras"
            element={
              <Suspense
                fallback={
                  <section className="cameras-page" aria-busy="true">
                    <p className="muted">Loading the cameras…</p>
                  </section>
                }
              >
                <GuestBlocked>
                  <Cameras />
                </GuestBlocked>
              </Suspense>
            }
          />
          <Route path="equipment" element={<Equipment />} />
          <Route
            path="toolkit"
            element={
              <Suspense
                fallback={
                  <div className="toolkit-loading" aria-busy="true">
                    Loading analyzer
                  </div>
                }
              >
                <GuestBlocked>
                  <MarineToolkit />
                </GuestBlocked>
              </Suspense>
            }
          />
          <Route
            path="displays"
            element={
              <GuestBlocked>
                <Displays />
              </GuestBlocked>
            }
          />
          <Route path="manuals" element={<Manuals />} />
          <Route path="settings" element={<BoatSettings />} />
          <Route path="live" element={<ModeBoardRedirect />} />
        </Route>

        {/* Chart-library gallery + composed helm example (unlisted; sample
          data, no auth). */}
        <Route
          path="/design/charts"
          element={
            <Suspense fallback={null}>
              <ChartLab />
            </Suspense>
          }
        />
        <Route
          path="/design/charts/helm"
          element={
            <Suspense fallback={null}>
              <HelmDemo />
            </Suspense>
          }
        />
        <Route
          path="/design/underway"
          element={
            <Suspense fallback={null}>
              <UnderwayDemo />
            </Suspense>
          }
        />

        {/* FTUE-v2 boat-reveal (public). */}
        <Route path="/reveal" element={<RevealEntry />} />
        {/* Admin console (ADR 0040) — its own auth provider + layout. */}
        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    </>
  );
}
